import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useCombinedStore } from "../store";
import * as AlfredoStyles from "../styles/components/Breadcrumb";
import { Page, Pages } from "../interfaces";

const pages: Pages[] = [
  {
    title: "Início",
    icon: "home",
    options: ["Dashboard", "Calendário"],
    path: "/",
  },
  {
    title: "Relatórios",
    icon: "picture_as_pdf",
    options: ["Contas", "Materiais"],
    path: "/reports",
    items: [
      {
        title: "Contas",
        icon: "list_alt",
        // `options` é DECORATIVO neste componente — não é renderizado. Quem
        // navega é `items[].path`. "Clientes" estava aqui em `options` desde
        // sempre e não tinha rota: aparecia como promessa e não levava a lugar
        // nenhum. Agora tem rota, e a rota explica o próprio bloqueio.
        options: ["Ordens", "Caixa", "Clientes"],
        path: "/reports/orders",
        items: [
          {
            title: "Ordens",
            icon: "receipt_long",
            options: [],
            path: "/reports/orders",
          },
          {
            title: "Caixa",
            icon: "point_of_sale",
            options: [],
            path: "/reports/cash",
          },
          {
            title: "Clientes",
            icon: "groups",
            options: [],
            path: "/reports/customers",
          },
        ],
      },
      {
        title: "Materiais",
        icon: "inventory",
        options: ["Estoque", "Movimentação"],
        path: "/reports/materials",
        items: [
          {
            title: "Estoque",
            icon: "inventory_2",
            options: [],
            path: "/reports/materials",
          },
          {
            title: "Movimentação",
            icon: "swap_horiz",
            options: [],
            path: "/reports/moviment",
          },
        ],
      },
    ],
  },
  {
    title: "Suprimentos",
    icon: "inventory",
    options: ["Compras", "Materiais"],
    path: "/supply",
    items: [
      {
        title: "Compras",
        icon: "local_shipping",
        options: ["Requisição", "Ordens"],
        path: "/supply/requisition",
      },
      {
        title: "Materiais",
        icon: "precision_manufacturing",
        options: ["Estoque", "Movimentação"],
        path: "/supply/moviment",
      },
    ],
  },
  {
    title: "Configuração",
    icon: "settings",
    // `options` é DADO MORTO neste componente: `renderPages` lê só
    // title/icon/path/items. A entrada anunciava "Usuários" e "Permissões" —
    // duas coisas que a tela real nem tem — e o item levava sempre para a
    // primeira aba, porque a aba não era legível pela URL.
    //
    // Agora as abas REAIS estão em `items`, com `path` que resolve: a
    // Configuração hidrata `?aba=` da URL, então cada linha daqui abre
    // exatamente a tela que promete.
    options: [
      "Lojas",
      "Cardápio",
      "Suprimentos",
      "Descontos e Taxas",
      "Comunicações",
      "Colaboradores",
      "Avançado",
    ],
    path: "/settings",
    items: [
      {
        title: "Lojas",
        icon: "storefront",
        options: [],
        path: "/settings?aba=Lojas",
      },
      {
        title: "Cardápio",
        icon: "restaurant_menu",
        options: [],
        path: "/settings?aba=Card%C3%A1pio",
      },
      {
        title: "Suprimentos",
        icon: "inventory",
        options: [],
        path: "/settings?aba=Suprimentos",
      },
      {
        title: "Descontos e Taxas",
        icon: "local_offer",
        options: [],
        path: "/settings?aba=Descontos%20e%20Taxas",
      },
      {
        title: "Comunicações",
        icon: "forum",
        options: [],
        path: "/settings?aba=Comunica%C3%A7%C3%B5es",
      },
      {
        title: "Colaboradores",
        icon: "badge",
        options: [],
        path: "/settings?aba=Colaboradores",
      },
      {
        title: "Avançado",
        icon: "tune",
        options: [],
        path: "/settings?aba=Avan%C3%A7ado",
      },
    ],
  },
];

const findPagePath = (
  path: string,
  pages: Pages[],
  parentPath: Pages[] = []
): Pages[] | null => {
  let foundPath = null;
  for (const page of pages) {
    const currentPath = [...parentPath, page];
    if (page.path === path) foundPath = currentPath;
    if (page.items) {
      const subPath = findPagePath(path, page.items, currentPath);
      if (subPath) foundPath = subPath;
    }
    if (foundPath) break;
  }
  return foundPath;
};

const Breadcrumb: React.FC = () => {
  const addPageZustand = useCombinedStore((state) => state.addPageZustand);
  const location = useLocation();

  useEffect(() => {
    const currentPagePath = findPagePath(location.pathname, pages);
    const page: any =
      currentPagePath && currentPagePath[currentPagePath.length - 1];
    // Verifica se a página é a página inicial antes de adicionar ao Zustand
    if (page && page.path !== "/") {
      const pageData: Page = {
        title: page.title,
        icon: page.icon,
        path: page.path,
      };
      addPageZustand(pageData);
    }
  }, [location, addPageZustand]);

  return (
    <AlfredoStyles.BreadcrumbContainer>
      {pages.map((page) => {
        const currentPagePath = findPagePath(location.pathname, [page]);
        if (currentPagePath) {
          return currentPagePath.map((breadcrumbPage) => (
            <AlfredoStyles.BreadcrumbItem key={breadcrumbPage.path}>
              <AlfredoStyles.BreadCrumbIcon
                selected={breadcrumbPage.path === location.pathname}
                color={
                  breadcrumbPage.path === location.pathname
                    ? "#008dea"
                    : "#888888"
                }
              >
                {breadcrumbPage.icon}
              </AlfredoStyles.BreadCrumbIcon>
              <AlfredoStyles.StyledLink
                to={breadcrumbPage.path}
                selected={breadcrumbPage.path === location.pathname}
                color={
                  breadcrumbPage.path === location.pathname
                    ? "#008dea"
                    : "#888888"
                }
              >
                {breadcrumbPage.title}
              </AlfredoStyles.StyledLink>
            </AlfredoStyles.BreadcrumbItem>
          ));
        }
        return null;
      })}
    </AlfredoStyles.BreadcrumbContainer>
  );
};

export default Breadcrumb;
